import { z } from 'zod';
import { apiClient } from '@api/client';
import type { CommonResponse } from '@/types/api';
import {
  marketPriceSchema,
  type ListMarketPricesParams,
  type MarketPrice,
} from './schema';

const BASE_PATH = '/market-prices';

const listResponseSchema = z.object({
  message: z.string(),
  columnDefs: z.array(z.record(z.unknown())).optional(),
  rowData: z.array(marketPriceSchema),
  gridOptions: z.record(z.unknown()).optional(),
  paginationDetails: z
    .object({
      page: z.number(),
      pageSize: z.number(),
      totalRecords: z.number(),
      isLastPage: z.boolean(),
    })
    .optional(),
});

const recordResponseSchema = z.object({
  statusCode: z.number(),
  message: z.string(),
  data: marketPriceSchema.nullable(),
  error: z.string().nullable().optional(),
});

export interface UpdateMarketPricePayload {
  minPrice: number;
  modalPrice: number;
  maxPrice: number;
  commodityArrivals: number;
}

function pruneParams(input: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(input)) {
    if (v === undefined || v === null || v === '') continue;
    out[k] = v;
  }
  return out;
}

export async function listMarketPrices(
  params: ListMarketPricesParams,
): Promise<CommonResponse<MarketPrice>> {
  const response = await apiClient.get(BASE_PATH, {
    params: pruneParams(params as unknown as Record<string, unknown>),
  });
  const parsed = listResponseSchema.safeParse(response.data);
  if (!parsed.success) {
    throw new Error(`Invalid list response: ${parsed.error.message}`);
  }
  return parsed.data;
}

export async function updateMarketPrice(
  id: string,
  payload: UpdateMarketPricePayload,
): Promise<MarketPrice> {
  const response = await apiClient.patch(`${BASE_PATH}/${id}`, payload);
  const parsed = recordResponseSchema.safeParse(response.data);
  if (!parsed.success || !parsed.data.data) {
    throw new Error(parsed.success ? 'Empty update response' : parsed.error.message);
  }
  return parsed.data.data;
}
